import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Grid,
  IconButton,
  Typography,
  Box,
  Alert,
  FormHelperText,
  Chip
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useCreateData } from 'services/queries';
import { PAYMENT_TYPE_CHOICES, TIME_BASED_PAYMENT_TYPES } from 'constants';

const PaymentTermForm = ({ open, onClose, template, onSuccess }) => {
  // Payment term templates cannot be edited after creation
  const isEditMode = false;
  
  const [formData, setFormData] = useState({
    is_active: true,
    components: [
      {
        payment_type: '',
        percentage: '',
        days_offset: '',
        order_index: 0
      }
    ]
  });

  const [errors, setErrors] = useState({});
  const [totalPercentage, setTotalPercentage] = useState(0);

  // Mutations
  const createMutation = useCreateData(
    'payment-terms/templates',
    ['payment-terms-templates'],
    {
      showNotification: true,
      onSuccess: () => {
        onSuccess();
      }
    }
  );

  // Template parameter is not used since templates cannot be edited

  useEffect(() => {
    // Calculate total percentage
    const total = formData.components.reduce((sum, comp) => {
      const percentage = parseInt(comp.percentage) || 0;
      return sum + percentage;
    }, 0);
    setTotalPercentage(total);
  }, [formData.components]);

  const handleComponentChange = (index, field, value) => {
    const updatedComponents = [...formData.components];
    updatedComponents[index] = {
      ...updatedComponents[index],
      [field]: value
    };

    // Clear days_offset if payment type doesn't require it
    if (field === 'payment_type') {
      const requiresDays = ['DAYS_AFTER_HBL_DATE', 'WITHIN_DAYS_AFTER_ETA'].includes(value);
      if (!requiresDays) {
        updatedComponents[index].days_offset = '';
      }
    }

    setFormData({ ...formData, components: updatedComponents });
  };

  const addComponent = () => {
    const newIndex = formData.components.length;
    setFormData({
      ...formData,
      components: [
        ...formData.components,
        {
          payment_type: '',
          percentage: '',
          days_offset: '',
          order_index: newIndex
        }
      ]
    });
  };

  const removeComponent = (index) => {
    const updatedComponents = formData.components.filter((_, i) => i !== index);
    // Update order_index for remaining components
    updatedComponents.forEach((comp, i) => {
      comp.order_index = i;
    });
    setFormData({ ...formData, components: updatedComponents });
  };

  const validateForm = () => {
    const newErrors = {};

    // Validate components
    if (formData.components.length === 0) {
      newErrors.components = 'At least one component is required';
    }

    formData.components.forEach((comp, index) => {
      if (!comp.payment_type) {
        newErrors[`component_${index}_type`] = 'Payment type is required';
      }
      if (!comp.percentage) {
        newErrors[`component_${index}_percentage`] = 'Percentage is required';
      } else if (parseInt(comp.percentage) <= 0 || parseInt(comp.percentage) > 100) {
        newErrors[`component_${index}_percentage`] = 'Percentage must be between 0 and 100';
      }

      // Validate days_offset for time-based types
      const requiresDays = ['DAYS_AFTER_HBL_DATE', 'WITHIN_DAYS_AFTER_ETA'].includes(comp.payment_type);
      if (requiresDays && comp.days_offset === '') {
        newErrors[`component_${index}_days`] = 'Days offset is required for this payment type';
      }
    });

    // Validate total percentage
    if (totalPercentage !== 100) {
      newErrors.total = `Total percentage must equal 100% (currently ${totalPercentage}%)`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      // Validation errors are already shown in the UI
      return;
    }

    // Prepare data for submission
    const submitData = {
      is_active: formData.is_active,
      components: formData.components.map(comp => ({
        payment_type: comp.payment_type,
        percentage: parseInt(comp.percentage),
        days_offset: comp.days_offset !== '' ? parseInt(comp.days_offset) : null,
        order_index: comp.order_index
      }))
    };

    createMutation.mutate(submitData);
  };

  const requiresDaysOffset = (paymentType) => {
    return ['DAYS_AFTER_HBL_DATE', 'WITHIN_DAYS_AFTER_ETA'].includes(paymentType);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        Create Payment Term Option
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {errors.total && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {errors.total}
            </Alert>
          )}

          <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">Components</Typography>
            <Chip
              label={`Total: ${totalPercentage}%`}
              color={totalPercentage === 100 ? 'success' : 'error'}
              variant="outlined"
            />
          </Box>

          {formData.components.map((component, index) => (
            <Box key={index} sx={{ mb: 3, p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth error={Boolean(errors[`component_${index}_type`])}>
                    <InputLabel>Payment Type</InputLabel>
                    <Select
                      value={component.payment_type}
                      onChange={(e) => handleComponentChange(index, 'payment_type', e.target.value)}
                      label="Payment Type"
                    >
                      {PAYMENT_TYPE_CHOICES.map((choice) => (
                        <MenuItem key={choice.value} value={choice.value}>
                          {choice.label}
                        </MenuItem>
                      ))}
                    </Select>
                    {errors[`component_${index}_type`] && (
                      <FormHelperText>{errors[`component_${index}_type`]}</FormHelperText>
                    )}
                  </FormControl>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <TextField
                    fullWidth
                    label="Percentage"
                    type="number"
                    value={component.percentage}
                    onChange={(e) => {
                      // Only allow integer values
                      const value = e.target.value;
                      if (value === '' || /^\d+$/.test(value)) {
                        handleComponentChange(index, 'percentage', value);
                      }
                    }}
                    onKeyDown={(e) => {
                      // Prevent decimal point and comma
                      if (e.key === '.' || e.key === ',') {
                        e.preventDefault();
                      }
                    }}
                    InputProps={{
                      endAdornment: '%',
                      inputProps: { 
                        min: 0, 
                        max: 100, 
                        step: 1,
                        pattern: '[0-9]*'
                      }
                    }}
                    error={Boolean(errors[`component_${index}_percentage`])}
                    helperText={errors[`component_${index}_percentage`]}
                  />
                </Grid>

                <Grid item xs={12} sm={3}>
                  {requiresDaysOffset(component.payment_type) ? (
                    <TextField
                      fullWidth
                      label="Days Offset"
                      type="number"
                      value={component.days_offset}
                      onChange={(e) => handleComponentChange(index, 'days_offset', e.target.value)}
                      required
                      error={Boolean(errors[`component_${index}_days`])}
                      helperText={errors[`component_${index}_days`] || ''}
                      InputProps={{
                        inputProps: { min: 0 }
                      }}
                    />
                  ) : (
                    <Chip
                      label="Days Offset not applicable"
                      variant="outlined"
                      color="default"
                      sx={{ width: '100%', height: 56, boxSizing: 'border-box', display: 'flex', alignItems: 'center', justifyContent: 'center', fontStyle: 'italic', opacity: 0.75 }}
                    />
                  )}
                </Grid>

                <Grid item xs={12} sm={2}>
                  <IconButton
                    color="error"
                    onClick={() => removeComponent(index)}
                    disabled={formData.components.length === 1}
                    title="Remove component"
                  >
                    <DeleteIcon />
                  </IconButton>
                </Grid>
              </Grid>
            </Box>
          ))}

          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={addComponent}
            fullWidth
            sx={{ mt: 1 }}
          >
            Add Component
          </Button>

          {errors.components && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {errors.components}
            </Alert>
          )}

          <Alert severity="info" sx={{ mt: 3 }}>
            <Typography variant="subtitle2">Auto-generated Information</Typography>
            <Typography variant="body2">
              The option name and code will be automatically generated based on the payment components you configure above.
            </Typography>
            <Typography variant="caption" display="block" sx={{ mt: 1 }}>
              Example: "30% Advanced + 70% Against HBL" will generate code "30ADV+70AHBL"
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button 
          onClick={handleSubmit} 
          variant="contained"
          disabled={createMutation.isLoading}
        >
          Create
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PaymentTermForm;